<script lang="ts" setup>
const { t, locale } = useI18n();
useHead({
  title: t("p-blog-index-title"),
  meta: [
    {
      name: "description",
      content: t("p-blog-index-meta-description"),
    },
    {
      name: "keywords",
      content: t("p-blog-index-meta-keywords"),
    },
  ],
});
import { Search, Plus } from "@element-plus/icons-vue";
import { fetchPosts, fetchArchivePosts } from "@/api";
import type { BlogItemList } from "@/shared/types";
import { ElMessage } from "element-plus";
import type { __String } from "typescript";
definePageMeta({ auth: false });
const localePath = useLocalePath();
const router = useRouter();
const route = useRoute();
const search = ref("");
const loading = ref(true);
const posts = ref<BlogItemList[]>([]);
const lastPage = ref(false);
const postsLimit = 30;
const { isAdmin, isModerator } = usePermission();

const searchPosts = async (passedParams?: Record<string, string | number>) => {
  loading.value = true;
  const query = {
    ...route.query,
  };
  delete query.search;
  if (search.value) {
    query.search = search.value;
  }
  router.replace({ query });
  try {
    const model: Record<string, string | number> = {
      query: search.value,
      limit: postsLimit,
      offset: passedParams?.offset || 0,
      locale: locale.value,
    };
    if (model.offset === 0) {
      posts.value = [];
      lastPage.value = false;
    }
    let res;
    if (activeTab.value === "archive" && (isAdmin || isModerator)) {
      res = await fetchArchivePosts(model);
    } else {
      res = await fetchPosts(model);
    }
    if (res.length < postsLimit) {
      lastPage.value = true;
    }
    posts.value = [...posts.value, ...res];
  } catch (error: any) {
    ElMessage.error(
      error?.data?.data?.message || t("p-blog-index-failed-to-fetch-posts")
    );
  }
  loading.value = false;
};
type ActiveTab = "active" | "archive";
const activeTab = ref<ActiveTab>((route.query.tab as ActiveTab) || "active");
onMounted(async () => {
  search.value = (route.query.search as string) || "";
  searchPosts();
});
const onTabChange = async () => {
  const newQuery = {
    ...route.query,
  };
  if (activeTab.value === "active") {
    delete newQuery.tab;
  } else {
    newQuery.tab = "archive";
  }
  await router.replace({ query: { ...newQuery } });
  searchPosts();
};
</script>

<template>
  <div class="default-width">
    <h1 class="text-center mb-7">{{ t("p-blog-index-solid-swap-blog") }}</h1>
    <el-backtop :right="30" :bottom="80" />
    <div class="text-center mb-10">
      <el-input
        class="search-box"
        v-model="search"
        :placeholder="t('p-blog-index-search-placeholder')"
        @keydown.enter="searchPosts()"
        size="large"
      >
        <template #append>
          <el-button round :icon="Search" @click="searchPosts()" />
        </template>
      </el-input>
      <div class="mt-2" v-if="isAdmin || isModerator">
        <el-button
          round
          @click="router.push(localePath('/blog/new'))"
          :icon="Plus"
          type="primary"
        >
          {{ t("p-blog-index-new-post") }}
        </el-button>
      </div>
      <el-tabs
        v-model="activeTab"
        v-if="isAdmin || isModerator"
        @tab-change="onTabChange()"
      >
        <el-tab-pane :label="t('p-blog-index-active')" name="active" />
        <el-tab-pane :label="t('p-blog-index-archive')" name="archive" />
      </el-tabs>
    </div>
    <div>
      <BlogItem v-for="post in posts" :key="post.idCode" v-bind="post" />

      <div class="text-center mt-10">
        <el-button
          round
          v-if="!lastPage && !loading"
          @click="searchPosts({ offset: posts.length })"
          type="primary"
        >
          {{ t("p-blog-index-load-more") }}
        </el-button>
      </div>
    </div>
    <div v-if="loading">
      <el-skeleton animated :count="2">
        <template #template>
          <el-row :gutter="20" align="middle" class="mb-3">
            <el-col :span="-1">
              <el-skeleton-item
                variant="image"
                style="width: 110px; height: 110px"
              />
            </el-col>
            <el-col :span="-1" class="flex-grow">
              <el-skeleton-item
                variant="text"
                class="mb-3"
                style="width: 33%"
              />
              <el-skeleton-item variant="text" />
              <el-skeleton-item variant="text" style="width: 13%" />
            </el-col>
          </el-row>
        </template>
      </el-skeleton>
    </div>
    <el-empty
      v-else-if="!posts.length"
      :description="t('p-blog-index-no-posts-found')"
    />
  </div>
</template>

<style scoped lang="scss">
.search-box {
  width: 60%;
  min-width: 280px;
}
</style>
